import type {
  CrmStageConfig,
  PersonalTaskBucket,
  TaskWithAssignments,
  Ticket,
} from '@scrambl/shared'
import { TaskStatusTypeEnum, TicketStatusTypeEnum } from '@scrambl/shared'

export type MyWorkTypeFilter = 'all' | 'tasks' | 'tickets'
export const MY_ORGANIZATION_ENTITY_FILTER_ID = '__my-organization__'
// The segmented control on My Work also hosts the Updates feed, which is not
// a work-item filter — keep it out of MyWorkTypeFilter so the bucket/filter
// pipeline never sees it.
export type MyWorkTab = MyWorkTypeFilter | 'updates'
export type MyWorkStatusCategory = 'open' | 'in_progress' | 'waiting' | 'done'

/**
 * The category headings the My Work status filter groups statuses under. Tasks
 * and tickets speak different status vocabularies (TaskStatusTypeEnum vs
 * TicketStatusTypeEnum); these four categories are the only vocabulary that
 * spans both, so they are what the picker groups by.
 */
export const MY_WORK_STATUS_CATEGORY_LABELS: Record<
  MyWorkStatusCategory,
  string
> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting: 'Waiting',
  done: 'Done',
}

export const MY_WORK_STATUS_CATEGORY_ORDER: MyWorkStatusCategory[] = [
  'open',
  'in_progress',
  'waiting',
  'done',
]
// 'done' is a manual-only parking bucket: it never comes from scheduling
// dates, only from an explicit user placement (drag or Move menu).
export type MyWorkAttentionBucket = TaskSourceBucket | 'done'
export type MyWorkAttentionFilter = 'all' | MyWorkAttentionBucket

export type TaskSourceBucket =
  | 'today'
  | 'tomorrow'
  | 'thisWeek'
  | 'nextWeek'
  | 'later'
  | 'unscheduled'
  | 'completedToday'

export type MyWorkBucketOrder = Partial<Record<MyWorkAttentionBucket, string[]>>
export type MyWorkBucketOverrides = Record<string, MyWorkAttentionBucket>

/** My Work's list groups, in display order, with their header colors. */
export const MY_WORK_BUCKETS: ReadonlyArray<{
  id: MyWorkAttentionBucket
  label: string
  color: string
}> = [
  { id: 'today', label: 'Today', color: '#6366f1' },
  { id: 'tomorrow', label: 'Tomorrow', color: '#2563eb' },
  { id: 'thisWeek', label: 'This week', color: '#16a34a' },
  { id: 'nextWeek', label: 'Next week', color: '#9333ea' },
  { id: 'later', label: 'Later', color: '#6b7280' },
  { id: 'unscheduled', label: 'Unscheduled', color: '#6b7280' },
  { id: 'done', label: 'Done', color: '#10b981' },
]

/** Preserve filtered-out cards without reintroducing the moved card in its old column. */
export function mergeHiddenTaskOrder(
  visibleOrder: Record<PersonalTaskBucket, string[]>,
  savedOrder: Partial<Record<PersonalTaskBucket, string[]>> | undefined,
  movedTaskId: string,
): Record<PersonalTaskBucket, string[]> {
  return Object.fromEntries(
    Object.entries(visibleOrder).map(([bucket, ids]) => [
      bucket,
      [
        ...ids,
        ...(savedOrder?.[bucket as PersonalTaskBucket] ?? []).filter(
          id => id !== movedTaskId && !ids.includes(id),
        ),
      ],
    ]),
  ) as Record<PersonalTaskBucket, string[]>
}

/**
 * The scheduledFor date a bucket move implies. Shared by My Work moves and
 * Focus-mode bucket moves so both surfaces reschedule identically (the date
 * is the durable signal — manual bucket placement falls back to it).
 */
export function getScheduledDateForBucket(
  bucket: PersonalTaskBucket,
): Date | null {
  const now = new Date()
  now.setHours(0, 0, 0, 0)
  switch (bucket) {
    case 'today': {
      const d = new Date(now)
      return d
    }
    case 'tomorrow': {
      const d = new Date(now)
      d.setDate(d.getDate() + 1)
      return d
    }
    case 'thisWeek': {
      const d = new Date(now)
      d.setDate(d.getDate() + Math.min(2, (7 - d.getDay()) % 7))
      return d
    }
    case 'nextWeek': {
      const d = new Date(now)
      const daysUntilNextMonday = (8 - d.getDay()) % 7 || 7
      d.setDate(d.getDate() + daysUntilNextMonday)
      return d
    }
    case 'later': {
      const d = new Date(now)
      const daysUntilNextMonday = (8 - d.getDay()) % 7 || 7
      d.setDate(d.getDate() + daysUntilNextMonday + 7)
      return d
    }
    case 'unscheduled':
    default:
      return null
  }
}

export function getResolvedTicketStage(
  stages: CrmStageConfig[],
): CrmStageConfig | undefined {
  return stages.find(
    stage => String(stage.stageTypeId) === TicketStatusTypeEnum.RESOLVED,
  )
}

/**
 * A top-level project task's position in its project task list: groups by
 * `group.order` ascending, tasks within a group by `task.order`, ungrouped
 * tasks after every group (matching buildGroupTableData's rendering). Only
 * comparable between items of the SAME project. Absent for tickets, personal
 * tasks, and subtasks (a subtask's order is scoped to its parent).
 */
export interface MyWorkProjectPosition {
  projectId: string
  ungrouped: boolean
  groupOrder: string
  taskOrder: string
}

export interface MyWorkItem {
  key: string
  moduleType: 'TASK' | 'TICKET'
  moduleId: string
  title: string
  identifier?: string
  typeLabel: 'Task' | 'Ticket'
  sourceStatus: string
  /**
   * Display name of the item's current status/stage ("In Review"), as opposed
   * to `sourceStatus` which holds the raw id. This is what the status filter
   * matches on: ids differ per project even when the name is identical, so
   * name is the only value that can filter across projects.
   */
  statusName: string
  statusCategory: MyWorkStatusCategory
  priority?: string | null
  attentionAt?: Date | string | null
  scheduledAt?: Date | string | null
  dueAt?: Date | string | null
  entityId?: string | null
  projectId?: string | null
  contextLabel?: string | null
  context?: {
    moduleType: string
    moduleId: string
    label: string
    secondaryLabel?: string | null
  } | null
  assigneeLabels: string[]
  attentionBucket: MyWorkAttentionBucket
  searchText?: string | null
  projectPosition?: MyWorkProjectPosition | null
  /**
   * Id of the project container the task sits in — sprint, cycle instance, or
   * bucket, whichever the task's project uses. Tickets have no container. The
   * container's own dates are not on the task payload; `applyContainerStaleness`
   * in sort-bucket.ts resolves them and stamps `containerEndAt`.
   */
  containerId?: string | null
  /**
   * End date of the item's container, set ONLY when that date has already
   * passed (an overrun sprint/cycle/bucket). Left null for containers still
   * running, undated buckets, containers we could not resolve, and tickets.
   */
  containerEndAt?: Date | string | null
}

/**
 * Apply manual My Work positions (`preferences.myWorkOrder`) within each
 * bucket: positioned items first, in saved order, the rest keep their
 * incoming order (the sort is stable). Done-today items are never reordered.
 */
export function sortByMyWorkOrder<
  T extends Pick<MyWorkItem, 'key' | 'attentionBucket'>,
>(items: T[], myWorkOrder: MyWorkBucketOrder): T[] {
  // Precomputed per-bucket position maps: order.indexOf inside the
  // comparator is O(|order|) per comparison and dominates large lists.
  const orderIndexByBucket = new Map<string, Map<string, number>>()
  Object.entries(myWorkOrder).forEach(([bucket, keys]) => {
    orderIndexByBucket.set(
      bucket,
      new Map((keys ?? []).map((key, index) => [key, index])),
    )
  })

  return [...items].sort((a, b) => {
    if (a.attentionBucket !== b.attentionBucket) return 0
    if (a.attentionBucket === 'completedToday') return 0
    const order = orderIndexByBucket.get(a.attentionBucket)
    const aIndex = order?.get(a.key) ?? -1
    const bIndex = order?.get(b.key) ?? -1
    if (aIndex === -1 && bIndex === -1) return 0
    if (aIndex === -1) return 1
    if (bIndex === -1) return -1
    return aIndex - bIndex
  })
}

export function validDate(value?: Date | string | null): Date | null {
  if (!value) return null
  const date =
    typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
      ? (() => {
          const [year, month, day] = value.split('-').map(Number)
          return new Date(year!, month! - 1, day)
        })()
      : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function startOfDay(value: Date): Date {
  const date = new Date(value)
  date.setHours(0, 0, 0, 0)
  return date
}

export function getAttentionBucket(
  attentionAt?: Date | string | null,
  fallback?: MyWorkAttentionBucket,
): MyWorkAttentionBucket {
  // Tasks have already been assigned to the user's native My Tasks bucket,
  // including any manual bucket override. Preserve it so Tasks, Tickets, and
  // All display the exact same grouping model.
  if (fallback) return fallback

  const attentionDate = validDate(attentionAt)
  if (!attentionDate) return 'unscheduled'

  const today = startOfDay(new Date())
  const day = startOfDay(attentionDate)
  if (day <= today) return 'today'

  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  if (day.getTime() === tomorrow.getTime()) return 'tomorrow'

  const endOfWeek = new Date(today)
  endOfWeek.setDate(endOfWeek.getDate() + ((7 - endOfWeek.getDay()) % 7))
  if (day <= endOfWeek) return 'thisWeek'

  const endOfNextWeek = new Date(endOfWeek)
  endOfNextWeek.setDate(endOfNextWeek.getDate() + 7)
  return day <= endOfNextWeek ? 'nextWeek' : 'later'
}

/** Keep an explicit This Week placement through Sunday, even when its date
 * overlaps Today/Tomorrow. It expires when that date is past or the week rolls. */
export function isTaskBucketPlacementCurrent(
  scheduledFor: Date | string | null | undefined,
  bucket: PersonalTaskBucket,
): boolean {
  if (!scheduledFor) return true
  const date = validDate(scheduledFor)
  if (!date) return false
  if (bucket === 'thisWeek') {
    const today = startOfDay(new Date())
    const endOfWeek = new Date(today)
    endOfWeek.setDate(endOfWeek.getDate() + ((7 - endOfWeek.getDay()) % 7))
    const day = startOfDay(date)
    return day >= today && day <= endOfWeek
  }
  return getAttentionBucket(scheduledFor) === bucket
}

export function applyMyWorkBucketOverride(
  item: MyWorkItem,
  override?: MyWorkAttentionBucket,
): MyWorkItem {
  // A manual placement (including Done) organizes active work only. Once the
  // underlying task/ticket is actually completed or canceled, the native
  // bucket wins again so finished work flows to Done Today / drops off the
  // list instead of lingering under a stale manual placement.
  if (!override || item.statusCategory === 'done') return item
  if (item.attentionBucket === override) return item
  return { ...item, attentionBucket: override }
}

export function clearMyWorkPlacement(
  workKey: string,
  order: MyWorkBucketOrder,
  overrides: MyWorkBucketOverrides,
): {
  order: MyWorkBucketOrder
  overrides: MyWorkBucketOverrides
  changed: boolean
} {
  let changed = Object.prototype.hasOwnProperty.call(overrides, workKey)
  const nextOverrides = { ...overrides }
  delete nextOverrides[workKey]

  const nextOrder = Object.fromEntries(
    Object.entries(order).map(([bucket, workKeys]) => {
      const filtered = workKeys?.filter(key => key !== workKey) ?? []
      if (filtered.length !== (workKeys?.length ?? 0)) changed = true
      return [bucket, filtered]
    }),
  ) as MyWorkBucketOrder

  return { order: nextOrder, overrides: nextOverrides, changed }
}

/**
 * Category for a task status TYPE. Takes the type id rather than a task so the
 * status filter can categorize a status config that no task currently sits on.
 */
export function statusTypeToCategory(
  statusTypeId?: string | null,
): MyWorkStatusCategory {
  if (
    statusTypeId === TaskStatusTypeEnum.COMPLETED ||
    statusTypeId === TaskStatusTypeEnum.CANCELED
  ) {
    return 'done'
  }
  if (statusTypeId === TaskStatusTypeEnum.IN_PROGRESS) return 'in_progress'
  if (statusTypeId === TaskStatusTypeEnum.ON_HOLD) return 'waiting'
  return 'open'
}

/** Category for a ticket stage TYPE. See statusTypeToCategory. */
export function stageTypeToCategory(
  stageTypeId?: string | null,
): MyWorkStatusCategory {
  if (
    stageTypeId === TicketStatusTypeEnum.RESOLVED ||
    stageTypeId === TicketStatusTypeEnum.CLOSED
  ) {
    return 'done'
  }
  if (stageTypeId === TicketStatusTypeEnum.IN_PROGRESS) return 'in_progress'
  if (
    stageTypeId === TicketStatusTypeEnum.WAITING_ON_INTERNAL ||
    stageTypeId === TicketStatusTypeEnum.WAITING_ON_REQUESTER
  ) {
    return 'waiting'
  }
  return 'open'
}

function taskStatusCategory(task: TaskWithAssignments): MyWorkStatusCategory {
  return statusTypeToCategory(task.statusTypeId)
}

function ticketStatusCategory(ticket: Ticket): MyWorkStatusCategory {
  return stageTypeToCategory(ticket.stageTypeId)
}

/**
 * Title-case a raw status id/slug for display, e.g. "in-progress" -> "In
 * Progress". Used when a status carries no resolved name.
 */
export function titleCaseStatusValue(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

/**
 * The status name shown on a task's row — resolved through the same chain
 * MyWorkTable renders the pill from, so the Status filter and the visible pill
 * can never disagree. A task can sit on a legacy status its type no longer
 * offers, hence the title-cased fallback.
 */
export function resolveTaskStatusName(task: TaskWithAssignments): string {
  const statusValue = task.status as unknown
  if (
    statusValue &&
    typeof statusValue === 'object' &&
    'name' in statusValue &&
    (statusValue as { name?: unknown }).name
  ) {
    return String((statusValue as { name?: unknown }).name)
  }

  const statusId = typeof statusValue === 'string' ? statusValue : undefined
  if (statusId) {
    const taskWithStatuses = task as TaskWithAssignments & {
      group?: { customStatuses?: Array<{ id: string; name: string }> | null }
      projectSettings?: {
        globalStatuses?: Array<{ id: string; name: string }> | null
      }
    }
    const candidates = [
      task.availableStatuses,
      taskWithStatuses.group?.customStatuses,
      taskWithStatuses.projectSettings?.globalStatuses,
    ]
    for (const statuses of candidates) {
      const match = statuses?.find(status => status.id === statusId)
      if (match?.name) return match.name
    }
    return titleCaseStatusValue(statusId)
  }

  return task.statusTypeId ? titleCaseStatusValue(task.statusTypeId) : 'Open'
}

/** Drop empty and repeated labels so one person never reads "Joel, Joel". */
function uniqueLabels(labels: Array<string | null>): string[] {
  const seen = new Set<string>()
  return labels.filter((label): label is string => {
    if (!label) return false
    const key = label.trim().toLowerCase()
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function userLabel(user: unknown): string | null {
  if (!user || typeof user !== 'object') return null
  const value = user as Record<string, unknown>
  const displayName =
    typeof value.displayName === 'string' ? value.displayName : ''
  const fullName = [value.firstName, value.lastName]
    .filter(part => typeof part === 'string' && part)
    .join(' ')
  const email = typeof value.email === 'string' ? value.email : ''
  return displayName || fullName || email || null
}

export function adaptTaskToWorkItem(
  task: TaskWithAssignments,
  sourceBucket: TaskSourceBucket,
): MyWorkItem {
  const attentionAt = task.scheduledFor || task.dueDate || null
  const statusValue = task.status as unknown
  const sourceStatus =
    typeof statusValue === 'string'
      ? statusValue
      : statusValue && typeof statusValue === 'object' && 'name' in statusValue
        ? String(
            (statusValue as { name?: unknown }).name ||
              task.statusTypeId ||
              'Open',
          )
        : String(task.statusTypeId || 'Open')
  const taskContext =
    task.moduleType !== 'MEMBER' && task.moduleId && task.moduleInfo
      ? {
          moduleType: String(task.moduleType),
          moduleId: task.moduleId,
          label: task.moduleInfo.name || 'Untitled record',
          secondaryLabel: task.moduleInfo.entity?.name || null,
        }
      : null
  const entityId = task.moduleInfo?.entity?.id || null
  const projectId =
    task.moduleType === 'PROJECT' && task.moduleId ? task.moduleId : null
  // Subtasks are skipped: their `order` is scoped to their parent, so it is
  // not comparable to top-level tasks' positions. `group` is untyped on
  // TaskWithAssignments, so read its order defensively.
  const groupOrder = (task.group as { order?: unknown } | null | undefined)
    ?.order
  const projectPosition: MyWorkProjectPosition | null =
    projectId && !task.parentTaskId
      ? {
          projectId,
          ungrouped: !task.groupId,
          groupOrder: typeof groupOrder === 'string' ? groupOrder : '',
          taskOrder: task.order || 'a1',
        }
      : null

  return {
    key: `TASK:${task.id}`,
    moduleType: 'TASK',
    moduleId: task.id,
    title: task.name || 'Untitled task',
    identifier: task.taskNumber || undefined,
    typeLabel: 'Task',
    sourceStatus,
    statusName: resolveTaskStatusName(task),
    statusCategory: taskStatusCategory(task),
    priority: task.priority,
    attentionAt,
    scheduledAt: task.scheduledFor,
    dueAt: task.dueDate,
    entityId,
    projectId,
    contextLabel: taskContext?.label || 'Personal',
    context: taskContext,
    assigneeLabels: uniqueLabels(
      (task.assignees ?? []).map(assignee =>
        userLabel((assignee as { user?: unknown }).user),
      ),
    ),
    attentionBucket:
      // "Completed today" and manually parked work share one Done lane.
      sourceBucket === 'completedToday'
        ? 'done'
        : getAttentionBucket(attentionAt, sourceBucket),
    searchText: task.description,
    projectPosition,
    // A project runs one of sprints, cycles or buckets; when a task somehow
    // carries more than one, follow the same precedence the project Tasks view
    // uses (useProjectTaskOrganization).
    containerId: task.sprintId || task.cycleInstanceId || task.bucketId || null,
  }
}

export function adaptTicketToWorkItem(
  ticket: Ticket,
  sourceBucket?: MyWorkAttentionBucket,
  // Stage id -> display name, built from the ticket type's stage config. Only
  // callers that render or filter stage names need to supply it; without it the
  // stage id is title-cased, which is what the row falls back to as well.
  stageNameById?: Map<string, string>,
): MyWorkItem {
  // An explicit due date is the user's scheduling choice. SLA remains the
  // fallback when no due date has been set.
  const attentionAt = ticket.dueDate || ticket.slaDueAt || null
  const stageValue = ticket.stage as unknown
  const sourceStatus =
    typeof stageValue === 'string'
      ? stageValue
      : stageValue && typeof stageValue === 'object' && 'name' in stageValue
        ? String(
            (stageValue as { name?: unknown }).name ||
              ticket.stageTypeId ||
              'Open',
          )
        : String(ticket.stageTypeId || 'Open')
  const ticketWithEntity = ticket as Ticket & {
    requesterEntity?: { id?: string; name?: string | null } | null
  }
  const referenceLabel = ticket.references?.find(
    reference => reference.label,
  )?.label
  const primaryReference = ticket.references?.[0]
  const projectReference = ticket.references?.find(
    reference => reference.moduleType === 'PROJECT',
  )
  const ticketContext = primaryReference?.moduleId
    ? {
        moduleType: String(primaryReference.moduleType),
        moduleId: primaryReference.moduleId,
        label: primaryReference.label || 'Linked record',
      }
    : ticketWithEntity.requesterEntity?.id
      ? {
          moduleType: 'ENTITY',
          moduleId: ticketWithEntity.requesterEntity.id,
          label: ticketWithEntity.requesterEntity.name || 'Client',
        }
      : null

  const statusCategory = ticketStatusCategory(ticket)
  const statusName =
    (typeof ticket.stage === 'string'
      ? stageNameById?.get(ticket.stage)
      : undefined) || titleCaseStatusValue(sourceStatus)

  return {
    key: `TICKET:${ticket.id}`,
    moduleType: 'TICKET',
    moduleId: ticket.id,
    title: ticket.subject || 'Untitled ticket',
    identifier: ticket.ticketNumber,
    typeLabel: 'Ticket',
    sourceStatus,
    statusName,
    statusCategory,
    priority: ticket.priority,
    attentionAt,
    // Tickets do not have a separate scheduledFor field. Their explicit due
    // date (or SLA fallback) is the date that places them on My Work.
    scheduledAt: attentionAt,
    dueAt: ticket.dueDate,
    entityId: ticketWithEntity.requesterEntity?.id || null,
    projectId: projectReference?.moduleId || null,
    contextLabel:
      ticketContext?.label || referenceLabel || ticket.requesterName,
    context: ticketContext,
    assigneeLabels: uniqueLabels(
      (ticket.assignments ?? []).map(assignment => userLabel(assignment.user)),
    ),
    attentionBucket:
      // Resolved/closed tickets belong in the Done lane, not a schedule bucket.
      statusCategory === 'done'
        ? 'done'
        : getAttentionBucket(attentionAt, sourceBucket),
    searchText: [
      ticket.description,
      ticket.requesterName,
      ticket.requesterEmail,
    ]
      .filter(Boolean)
      .join(' '),
  }
}

export interface MyWorkFilterOptions {
  search?: string
  /**
   * Status/stage display names to keep, matched case-insensitively. Empty means
   * no status filter. Names rather than ids so one selection spans every
   * project that defines a status by that name.
   */
  statusNames?: string[]
  priorityFilter?: string
  attentionFilter?: MyWorkAttentionFilter
  entityIds?: string[]
  /** Project ids owned by the current organization, used by the special
   * "My Organization" choice in the client filter. */
  organizationProjectIds?: string[]
  projectIds?: string[]
  scheduledForFrom?: string
  scheduledForTo?: string
  dueDateFrom?: string
  dueDateTo?: string
  atRisk?: boolean
  overdue?: boolean
  priorityDeferred?: boolean
}

const ATTENTION_BUCKET_ORDER: Record<MyWorkAttentionBucket, number> = {
  today: 0,
  tomorrow: 1,
  thisWeek: 2,
  nextWeek: 3,
  later: 4,
  unscheduled: 5,
  completedToday: 6,
  done: 6,
}

export function isMyWorkItemOverdue(
  item: MyWorkItem,
  now = new Date(),
): boolean {
  if (item.statusCategory === 'done') return false
  const deadline = validDate(
    item.dueAt || (item.moduleType === 'TICKET' ? item.attentionAt : null),
  )
  if (!deadline) return false
  return startOfDay(deadline) < startOfDay(now)
}

export function isMyWorkItemAtRisk(item: MyWorkItem): boolean {
  if (item.statusCategory === 'done' || !item.dueAt) return false
  const dueBucket = getAttentionBucket(item.dueAt)
  return (
    ATTENTION_BUCKET_ORDER[item.attentionBucket] >
    ATTENTION_BUCKET_ORDER[dueBucket]
  )
}

const DEFERRABLE_PRIORITIES = new Set(['URGENT', 'HIGH'])

/**
 * An URGENT/HIGH item parked in Later/Unscheduled: the user's schedule and
 * the project's prioritization disagree. Deliberately no cross-item
 * comparison — sequencing lower-priority work first WITHIN a near-term
 * bucket is a normal choice, not neglect.
 */
export function isMyWorkItemPriorityDeferred(item: MyWorkItem): boolean {
  if (item.statusCategory === 'done') return false
  if (!DEFERRABLE_PRIORITIES.has(item.priority || '')) return false
  return (
    item.attentionBucket === 'later' || item.attentionBucket === 'unscheduled'
  )
}

function matchesDateRange(
  value: Date | string | null | undefined,
  from: string | undefined,
  to: string | undefined,
): boolean {
  if (!from && !to) return true
  const date = validDate(value)
  if (!date) return false

  const fromDate = validDate(from)
  const toDate = validDate(to)
  if (fromDate) fromDate.setHours(0, 0, 0, 0)
  if (toDate) toDate.setHours(23, 59, 59, 999)

  if (fromDate && date < fromDate) return false
  if (toDate && date > toDate) return false
  return true
}

/**
 * The single filter predicate for My Work items — MyWorkTable, MyWorkDeck and
 * the board must all agree on what the active filters show.
 */
export function matchesMyWorkFilters(
  item: MyWorkItem,
  {
    search = '',
    statusNames = [],
    priorityFilter = 'all',
    attentionFilter = 'all',
    entityIds = [],
    organizationProjectIds = [],
    projectIds = [],
    scheduledForFrom,
    scheduledForTo,
    dueDateFrom,
    dueDateTo,
    atRisk = false,
    overdue = false,
    priorityDeferred = false,
  }: MyWorkFilterOptions,
): boolean {
  if (
    statusNames.length > 0 &&
    !statusNames.some(
      name => name.toLowerCase() === item.statusName.toLowerCase(),
    )
  ) {
    return false
  }
  if (priorityFilter !== 'all' && item.priority !== priorityFilter) {
    return false
  }
  if (attentionFilter !== 'all' && item.attentionBucket !== attentionFilter) {
    return false
  }
  if (entityIds.length > 0) {
    const regularEntityIds = entityIds.filter(
      id => id !== MY_ORGANIZATION_ENTITY_FILTER_ID,
    )
    const matchesEntity = regularEntityIds.includes(item.entityId || '')
    const matchesOrganizationProject =
      entityIds.includes(MY_ORGANIZATION_ENTITY_FILTER_ID) &&
      Boolean(item.projectId && organizationProjectIds.includes(item.projectId))

    if (!matchesEntity && !matchesOrganizationProject) return false
  }
  if (projectIds.length > 0 && !projectIds.includes(item.projectId || '')) {
    return false
  }
  if (!matchesDateRange(item.scheduledAt, scheduledForFrom, scheduledForTo)) {
    return false
  }
  if (!matchesDateRange(item.dueAt, dueDateFrom, dueDateTo)) return false
  if (
    (atRisk || overdue || priorityDeferred) &&
    !(
      (atRisk && isMyWorkItemAtRisk(item)) ||
      (overdue && isMyWorkItemOverdue(item)) ||
      (priorityDeferred && isMyWorkItemPriorityDeferred(item))
    )
  ) {
    return false
  }
  const needle = search.trim().toLowerCase()
  if (!needle) return true
  return [
    item.title,
    item.identifier,
    item.contextLabel,
    item.sourceStatus,
    item.typeLabel,
    item.searchText,
  ]
    .filter(Boolean)
    .some(value => String(value).toLowerCase().includes(needle))
}
